import type { ChangeEvent } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Select, SelectOption } from '@/common/ui/Form/Select'

interface PaginationProps {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    className?: string
}

export const Pagination = ({ currentPage, totalPages, onPageChange, className }: PaginationProps) => {
    const getPageNumbers = () => {
        const pages = []
        const maxVisiblePages = 5
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return pages
    }

    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        onPageChange(Number(e.target.value))
    }

    const handlePrevClick = () => {
        onPageChange(currentPage - 1)
    }

    const handleNextClick = () => {
        onPageChange(currentPage + 1)
    }

    const pageNumbers = getPageNumbers()

    if (totalPages <= 1) {
        return null
    }

    return (
        <>
            {/* Мобильная пагинация: стрелки + Select */}
            <div className={cn('join items-center sm:hidden', className)}>
                <button
                    type='button'
                    onClick={handlePrevClick}
                    disabled={currentPage === 1}
                    className='join-item btn'
                    aria-label='Предыдущая страница'
                >
                    ←
                </button>
                <Select
                    value={String(currentPage)}
                    onChange={handleSelectChange}
                    size='lg'
                    variant='outline'
                    emphasis='neutral'
                    showChevron
                    aria-label='Выбор страницы'
                    className='join-item min-w-20 !rounded-none ml-[0px]'
                >
                    {Array.from({ length: totalPages }, (_, i) => {
                        const page = i + 1
                        return (
                            <SelectOption key={page} value={String(page)}>
                                {page}
                            </SelectOption>
                        )
                    })}
                </Select>
                <button
                    type='button'
                    onClick={handleNextClick}
                    disabled={currentPage === totalPages}
                    className='join-item btn'
                    aria-label='Следующая страница'
                >
                    →
                </button>
            </div>

            {/* Десктопная пагинация: стрелки + номера */}
            <div className={cn('join hidden items-center sm:inline-flex', className)}>
                <button
                    type='button'
                    onClick={handlePrevClick}
                    disabled={currentPage === 1}
                    className='join-item btn'
                    aria-label='Предыдущая страница'
                >
                    ←
                </button>

                {pageNumbers[0] > 1 && (
                    <>
                        <button type='button' onClick={() => onPageChange(1)} className='join-item btn'>
                            1
                        </button>
                        {pageNumbers[0] > 2 && (
                            <button type='button' disabled className='join-item btn' aria-hidden>
                                ...
                            </button>
                        )}
                    </>
                )}

                {pageNumbers.map(page => (
                    <button
                        type='button'
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={cn('join-item btn', {
                            'btn-active': page === currentPage,
                        })}
                        aria-label={`Страница ${page}`}
                        aria-current={page === currentPage ? 'page' : undefined}
                    >
                        {page}
                    </button>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                    <>
                        {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                            <button type='button' disabled className='join-item btn' aria-hidden>
                                ...
                            </button>
                        )}
                        <button type='button' onClick={() => onPageChange(totalPages)} className='join-item btn'>
                            {totalPages}
                        </button>
                    </>
                )}

                <button
                    type='button'
                    onClick={handleNextClick}
                    disabled={currentPage === totalPages}
                    className='join-item btn'
                    aria-label='Следующая страница'
                >
                    →
                </button>
            </div>
        </>
    )
}
